import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  SparklesIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';

interface PublicLayoutProps {
  children: React.ReactNode;
  /**
   * Render the header with `position: fixed` and a transparent background
   * over the page hero. The background fades to dark+blur once the user
   * scrolls past the hero. Set on every public marketing page.
   */
  transparentHeader?: boolean;
  /**
   * When true, the nav logo starts invisible and fades in only after the
   * user scrolls past the hero. Used exclusively on the home page, where a
   * hero-prominence logo sits in the hero and "hands off" to the nav mark
   * on scroll. Other pages keep the nav logo visible at all times — they
   * have no hero logo to coordinate with.
   *
   * Defaults to false so any new page is visible-from-the-start by default.
   */
  defersNavLogoToHeroScroll?: boolean;
}

/**
 * Unified public layout component for all marketing/public pages.
 * Provides consistent header navigation and footer across the site.
 *
 * SaaS UX Best Practices implemented:
 * - Consistent navigation across all pages
 * - Clear product hierarchy in navigation
 * - Prominent CTAs (Sign in / Get Started)
 * - Comprehensive footer with all important links
 * - Mobile-responsive design
 * - Language switcher accessibility
 */
const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  transparentHeader = false,
  defersNavLogoToHeroScroll = false,
}) => {
  const { t } = useTranslation(['common', 'landing']);
  const location = useLocation();

  // Track scroll past the hero, regardless of whether the nav logo defers
  // to it. The header background fades to solid+blur on every transparent-
  // header page once the user leaves the hero. The nav logo's visibility
  // only depends on this flag when defersNavLogoToHeroScroll is true.
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  useEffect(() => {
    if (!transparentHeader) return undefined;
    const onScroll = () => setScrolledPastHero(window.scrollY > 200);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [transparentHeader]);
  const navLogoVisible = !defersNavLogoToHeroScroll || scrolledPastHero;

  // React Router v7 doesn't trigger the browser's native anchor-scroll
  // behavior for in-app links (Link, navigate). When a hash is present in
  // the URL, manually scroll the matching element into view. Combined with
  // the scroll-margin-top utility on DeckSection, this makes #anchor links
  // work without being hidden under the fixed nav header.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      // Wait one frame so the layout has settled (especially on first paint
      // when the page is still mounting and DeckSection elements haven't
      // measured their padding).
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash, location.pathname]);

  // Product links for dropdown
  const products = [
    {
      name: t('landing:products.comply.name'),
      description: t('landing:products.comply.description'),
      href: '/compliance',
      icon: ShieldCheckIcon,
    },
    {
      name: t('landing:products.quality.name'),
      description: t('landing:products.quality.description'),
      href: '/pricing?product=quality',
      icon: ChartBarIcon,
    },
    {
      name: t('landing:products.full.name'),
      description: t('landing:products.full.description'),
      href: '/pricing?product=full',
      icon: SparklesIcon,
    },
  ];

  // Main navigation links
  const navigation = [
    { name: t('landing:navigation.products'), href: '#', hasDropdown: true },
    { name: t('landing:navigation.pricing'), href: '/pricing' },
    { name: t('landing:navigation.about'), href: '/about' },
    { name: t('landing:navigation.docs'), href: 'https://docs.callcov.com', external: true },
  ];

  // Footer sections
  const footerSections = [
    {
      title: t('landing:footer.product.title'),
      links: [
        { name: 'COMPLY', href: '/compliance' },
        { name: 'QUALITY', href: '/pricing?product=quality' },
        { name: 'FULL', href: '/pricing?product=full' },
        { name: t('landing:navigation.pricing'), href: '/pricing' },
      ],
    },
    {
      title: t('landing:footer.company.title'),
      links: [
        { name: t('landing:footer.company.about'), href: '/about' },
        { name: t('landing:footer.company.contact'), href: '/contact' },
        { name: t('landing:footer.resources.support'), href: '/support' },
      ],
    },
    {
      title: t('landing:footer.resources.title'),
      links: [
        { name: t('landing:footer.resources.documentation'), href: 'https://docs.callcov.com', external: true },
        { name: t('landing:footer.resources.apiReference'), href: 'https://docs.callcov.com/api/overview', external: true },
        { name: t('landing:footer.resources.status'), href: 'https://status.callcov.com', external: true },
      ],
    },
    {
      title: t('landing:footer.legal.title'),
      links: [
        { name: t('landing:footer.legal.privacy'), href: '/privacy' },
        { name: t('landing:footer.legal.terms'), href: '/terms' },
      ],
    },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      {/* Smart-sticky header:
          - Landing pages (transparentHeader=true) use position:fixed so the
            nav stays pinned from the start but begins visually transparent
            over the hero. Once the user scrolls past the hero logo, the
            background fades in (dark + blur + divider) so the nav becomes
            a clear persistent surface for conversion actions.
          - Non-landing pages use position:sticky with the solid background
            from the start — no scroll state needed there.
          The bg transition is 300ms ease-out, matching the logo fade-in
          timing so both arrive together. */}
      <Disclosure
        as="header"
        className={`${transparentHeader ? 'fixed' : 'sticky'} inset-x-0 top-0 z-50`}
      >
        {({ open }) => (
          <>
            <nav
              className={`flex items-center justify-between py-4 px-6 lg:px-8 transition-[background-color,border-color,backdrop-filter] duration-300 ease-out ${
                !transparentHeader || scrolledPastHero || open
                  ? 'bg-black/80 backdrop-blur-md border-b border-white/10'
                  : 'bg-transparent border-b border-transparent'
              }`}
              aria-label="Global"
            >
              <div className="flex lg:flex-1">
                <Link
                  to="/"
                  className="-m-1.5 p-1.5 group inline-flex items-center"
                  aria-label="CallCov, inicio"
                  aria-hidden={!navLogoVisible}
                  tabIndex={navLogoVisible ? 0 : -1}
                >
                  {/* The SVG composition is icon-dominant with the wordmark
                      offset to the lower-right (wordmark vertical center at
                      68% of image height, not 50%). The inner translate-y
                      shifts the image up by ~18% of its height so the
                      wordmark lands on the flex row center — aligned with
                      the nav text baseline.
                      On landing pages (transparentHeader) the nav logo
                      starts invisible and fades in with a 4px slide once
                      the hero logo has scrolled past. */}
                  <img
                    src="/logos/callcov-white.svg"
                    alt="CallCov"
                    className={`h-14 w-auto -translate-y-[11px] transition-[opacity,transform] duration-300 ease-out will-change-[opacity,transform] ${
                      navLogoVisible
                        ? 'opacity-100'
                        : 'opacity-0 -translate-y-[15px]'
                    }`}
                  />
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex lg:gap-x-8 lg:items-center">
                {navigation.map((item) =>
                  item.hasDropdown ? (
                    <div key={item.name} className="relative group">
                      <button className="flex items-center gap-1 text-sm font-medium leading-6 text-white/80 hover:text-white transition-colors">
                        {item.name}
                        <ChevronDownIcon className="w-4 h-4 transition-transform group-hover:rotate-180" />
                      </button>
                      {/* Dropdown */}
                      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        <div className="w-72 bg-neutral-900 border border-white/10 overflow-hidden">
                          {products.map((product) => (
                            <Link
                              key={product.name}
                              to={product.href}
                              className="flex items-start gap-4 p-4 hover:bg-white/5 transition-colors"
                            >
                              <div className="w-10 h-10 bg-white/5 flex items-center justify-center flex-shrink-0">
                                <product.icon className="w-5 h-5 text-sky-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white">
                                  {product.name}
                                </p>
                                <p className="text-xs text-white/50 mt-0.5">
                                  {product.description}
                                </p>
                              </div>
                            </Link>
                          ))}
                          <div className="border-t border-white/10 p-4 bg-white/5">
                            <Link
                              to="/pricing"
                              className="text-sm font-medium text-sky-400 hover:text-sky-300"
                            >
                              {t('landing:navigation.compareProducts')} &rarr;
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : item.external ? (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium leading-6 text-white/80 hover:text-white transition-colors"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`text-sm font-medium leading-6 transition-colors ${
                        isActive(item.href)
                          ? 'text-sky-400'
                          : 'text-white/80 hover:text-white'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )
                )}
              </div>

              {/* Desktop Actions */}
              <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-4 lg:items-center">
                <LanguageSwitcher />
                <Link
                  to="/signin"
                  className="text-sm font-medium leading-6 text-white/80 hover:text-white transition-colors px-3 py-2"
                >
                  {t('landing:navigation.signIn')} <span aria-hidden="true">&rarr;</span>
                </Link>
                <Link
                  to="/signup"
                  className="text-sm font-medium px-5 py-2.5 bg-sky-600 hover:bg-sky-500 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                >
                  {t('landing:navigation.getStarted')}
                </Link>
              </div>

              {/* Mobile menu button */}
              <div className="flex lg:hidden gap-x-3 items-center">
                <LanguageSwitcher />
                <DisclosureButton className="inline-flex items-center justify-center p-2.5 text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500">
                  <span className="sr-only">{open ? t('landing:navigation.closeMenu') : t('landing:navigation.openMenu')}</span>
                  {open ? (
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              </div>
            </nav>

            {/* Mobile menu panel */}
            <DisclosurePanel className="lg:hidden">
              <div className="space-y-1 px-4 pb-3 pt-2 bg-black border-t border-white/10">
                {/* Products section */}
                <div className="py-2">
                  <p className="px-3 text-xs font-semibold text-white/60 uppercase tracking-wide mb-2">
                    {t('landing:navigation.products')}
                  </p>
                  {products.map((product) => (
                    <Link
                      key={product.name}
                      to={product.href}
                      className="flex items-center gap-3 px-3 py-2 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <product.icon className="w-5 h-5 text-sky-400" />
                      {product.name}
                    </Link>
                  ))}
                </div>

                <div className="border-t border-white/10 py-2">
                  <Link
                    to="/pricing"
                    className="block px-3 py-2 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                  >
                    {t('landing:navigation.pricing')}
                  </Link>
                  <Link
                    to="/about"
                    className="block px-3 py-2 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                  >
                    {t('landing:navigation.about')}
                  </Link>
                  <a
                    href="https://docs.callcov.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                  >
                    {t('landing:navigation.docs')}
                  </a>
                </div>

                {/* Mobile auth buttons */}
                <div className="border-t border-white/10 pt-4 space-y-2">
                  <Link
                    to="/signin"
                    className="block px-3 py-2 text-base font-medium text-white/80 hover:bg-white/5 hover:text-white transition-colors"
                  >
                    {t('landing:navigation.signIn')}
                  </Link>
                  <Link
                    to="/signup"
                    className="block px-3 py-2 text-base font-medium bg-sky-600 text-white hover:bg-sky-500 transition-colors text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                  >
                    {t('landing:navigation.getStarted')}
                  </Link>
                </div>
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 text-white">
        <div className="container-custom py-16">
          {/* Footer Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {footerSections.map((section) => (
              <div key={section.title}>
                <h3 className="font-medium mb-4 text-white">{section.title}</h3>
                <ul className="space-y-2 text-sm">
                  {section.links.map((link) =>
                    'external' in link && link.external ? (
                      <li key={link.name}>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/50 hover:text-white transition-colors"
                        >
                          {link.name}
                        </a>
                      </li>
                    ) : (
                      <li key={link.name}>
                        <Link
                          to={link.href}
                          className="text-white/50 hover:text-white transition-colors"
                        >
                          {link.name}
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              </div>
            ))}
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="inline-flex items-center" aria-label="CallCov, inicio">
                <img
                  src="/logos/callcov-white.svg"
                  alt="CallCov"
                  className="h-10 w-auto -translate-y-[7px]"
                />
              </Link>
              <p className="text-sm text-white/50">
                {t('landing:footer.copyright')}
              </p>
            </div>
            <div className="flex items-center gap-6">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
